import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import { MDX } from "./mdx/tokens";

/**
 * TestimonialsRibbon — 5-second auto-rotating quote strip.
 *
 * ⚠️ PLACEHOLDER COPY BELOW — swap in real trader quotes when available.
 * All quotes are marked with `placeholder: true` so a build-time linter
 * can flag them for replacement before the marketing team ships.
 */
const TESTIMONIALS = [
    {
        placeholder: true,
        quote:  "I went from chasing my losses 6 times a week to just 1. The app slows me down before I make it worse.",
        author: "Amina K.",
        role:   "Learning to trade · Nairobi",
        flag:   "🇰🇪",
        result: "-47% impulse trades",
    },
    {
        placeholder: true,
        quote:  "Obondo showed me a mistake I'd been making for eight months without knowing it. Worth trying just for that.",
        author: "Youssef M.",
        role:   "New investor · Cairo",
        flag:   "🇪🇬",
        result: "+31% better planning",
    },
    {
        placeholder: true,
        quote:  "The Sheng tutor finally helped me understand how short-selling works — betting a price will go down. First app that spoke my language.",
        author: "Kevin O.",
        role:   "University trader · Lagos",
        flag:   "🇳🇬",
        result: "Journal 98%",
    },
];

export default function TestimonialsRibbon({ isDark }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 5000);
        return () => clearInterval(t);
    }, []);

    const t = TESTIMONIALS[idx];
    const bg = isDark ? "bg-[#0B1120]" : "bg-[#F9FAFB]";
    const brd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";

    return (
        <section
            className={`${bg} ${text} border-y ${brd} py-10 md:py-14`}
            data-testid="testimonials-ribbon"
        >
            <div className="max-w-[1000px] mx-auto px-4 md:px-8">
                <div key={idx} className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 animate-fade-in">
                    <Quote className="h-8 w-8 shrink-0" style={{ color: MDX.blueLow }} />
                    <div className="flex-1">
                        <p className="font-inter font-medium leading-snug" style={{ fontSize: "clamp(18px, 2.2vw, 22px)" }} data-testid="testimonial-quote">
                            &ldquo;{t.quote}&rdquo;
                        </p>
                        <div className={`mt-3 font-roboto text-[14px] flex flex-wrap items-center gap-2 ${dim}`}>
                            <span>{t.flag}</span>
                            <b className={text}>{t.author}</b>
                            <span>·</span>
                            <span>{t.role}</span>
                            <span>·</span>
                            <span className="font-mono proportional-nums" style={{ color: MDX.blueLow }}>{t.result}</span>
                        </div>
                    </div>
                    <div className="flex gap-1.5" data-testid="testimonials-dots">
                        {TESTIMONIALS.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                aria-label={`Show testimonial ${i + 1}`}
                                data-testid={`testimonial-dot-${i}`}
                                className="h-1.5 rounded-full transition-all"
                                style={{
                                    width: i === idx ? 24 : 8,
                                    background: i === idx ? MDX.blueLowSolid : (isDark ? "#374151" : "#D1D5DB"),
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
